import { FaPlay, FaFilePdf, FaFileWord, FaVideo, FaImage, FaFile, FaDownload, FaClock, FaFolder, FaTrash, FaEdit } from "react-icons/fa"
import { ResourceFile, ResourceType } from "../Interfaces"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useResourceCard } from "../hooks/useResourceCard"
import { DescriptionDialog } from "./DescriptionDialog"

// Card for each resource
export function ResourceCard({
    resource,
    onOpenVideo,
    onDelete,
    resourceType,
    onResourceRefresh
}: {
    resource: ResourceFile;
    onOpenVideo: (fileName: string) => void;
    onDelete: (fileName: string, type: ResourceType) => void;
    resourceType: ResourceType;
    onResourceRefresh?: () => void;
}) {
    const {
        isHovered,
        showDeleteDialog,
        showDescriptionDialog,
        handlePlayClick,
        handleDownload,
        handleDelete,
        handleConfirmDelete,
        handleCancelDelete,
        handleEditDescription,
        handleCloseDescriptionDialog,
        handleDescriptionUpdated,
        handleMouseEnter,
        handleMouseLeave
    } = useResourceCard({
        resource,
        onOpenVideo,
        onDelete,
        resourceType,
        onResourceRefresh
    })

    return (
        <CardContainer 
            isHovered={isHovered}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handlePlayClick}
        >
            <CardBackgroundOverlay />
            <CardContent>
                <FileIconSection resource={resource} />
                <FileDetailsSection resource={resource} />
                <FileActionsSection resource={resource} onDownload={handleDownload} onDelete={handleDelete} onEditDescription={handleEditDescription} />
            </CardContent>
            
            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog 
                isOpen={showDeleteDialog}
                fileName={resource.name}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
            />
            
            {/* Description Edit Dialog */}
            <DescriptionDialog
                isOpen={showDescriptionDialog}
                onClose={handleCloseDescriptionDialog}
                fileName={resource.name}
                resourceType={resourceType}
                currentDescription={resource.description || ""}
                onDescriptionUpdated={handleDescriptionUpdated}
            />
        </CardContainer>
    )
}

// Main card container with hover effects and interactions
function CardContainer({ 
    isHovered, 
    onMouseEnter, 
    onMouseLeave, 
    onClick, 
    children 
}: {
    isHovered: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <div 
            className={`
                relative bg-white rounded-xl shadow-md border border-gray-200 p-6 w-full h-40 
                transition-all duration-300 ease-in-out cursor-pointer group
                ${isHovered ? 'shadow-xl border-orange-300 transform scale-[1.02]' : ''}
                hover:shadow-xl hover:border-orange-300 hover:transform hover:scale-[1.02]
            `}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
        >
            {children}
        </div>
    )
}

// Background gradient overlay that appears on hover
function CardBackgroundOverlay() {
    return (
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    )
}

// Main content wrapper for card layout
function CardContent({ children }: { children: React.ReactNode }) {
    return (
        <div className="relative flex items-center h-full">
            {children}
        </div>
    )
}

// Left section with file icon and optional play button
function FileIconSection({ resource }: { resource: ResourceFile }) {
    return (
        <div className="flex items-center justify-center min-w-0 flex-shrink-0 mr-6">
            <FileIconContainer>
                <FileTypeIcon type={resource.type} />
                {resource.type === 'mp4' && <PlayButtonOverlay />}
            </FileIconContainer>
        </div>
    )
}

// Container for file type icon with hover effects
function FileIconContainer({ children }: { children: React.ReactNode }) {
    return (
        <div className="relative">
            <div className="flex items-center justify-center w-16 h-16 bg-gray-50 rounded-xl border-2 border-gray-200 group-hover:border-orange-300 transition-all duration-300">
                {children}
            </div>
        </div>
    )
}

// File type icon based on file extension
function FileTypeIcon({ type }: { type: string }) {
    const getFileIcon = (fileType: string) => {
        switch (fileType.toLowerCase()) {
            case 'pdf':
                return <FaFilePdf className="w-8 h-8 text-red-500" />
            case 'docx':
                return <FaFileWord className="w-8 h-8 text-blue-500" />
            case 'mp4':
                return <FaVideo className="w-8 h-8 text-purple-500" />
            case 'jpg':
            case 'png':
                return <FaImage className="w-8 h-8 text-green-500" />
            default:
                return <FaFile className="w-8 h-8 text-gray-500" />
        }
    }

    return getFileIcon(type)
}

// Play button overlay for video files
function PlayButtonOverlay() {
    return (
        <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-orange-600 rounded-full p-2 shadow-lg group-hover:bg-orange-700 transition-colors duration-200">
                <FaPlay className="w-4 h-4 text-white ml-0.5" />
            </div>
        </div>
    )
}

// Middle section with file name, type badge, and metadata
function FileDetailsSection({ resource }: { resource: ResourceFile }) {
    return (
        <div className="flex-1 min-w-0 mr-4">
            <div className="flex flex-col justify-center h-full">
                <FileName name={resource.name} />
                <FileTypeBadge type={resource.type} />
                <FileMetadata lastModified={resource.last_modified} description={resource.description} />
            </div>
        </div>
    )
}

// File name with hover color change
function FileName({ name }: { name: string }) {
    return (
        <h3 className="text-lg font-semibold text-gray-900 truncate mb-1 group-hover:text-orange-700 transition-colors duration-200">
            {name}
        </h3>
    )
}

// Color-coded file type badge
function FileTypeBadge({ type }: { type: string }) {
    const getBadgeColors = (fileType: string) => {
        switch (fileType) {
            case 'pdf': return 'bg-red-100 text-red-700'
            case 'docx': return 'bg-blue-100 text-blue-700'
            case 'mp4': return 'bg-purple-100 text-purple-700'
            case 'jpg':
            case 'png': return 'bg-green-100 text-green-700'
            default: return 'bg-gray-100 text-gray-700'
        }
    }

    return (
        <div className="flex items-center gap-2 mb-2">
            <span className={`
                inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wide
                ${getBadgeColors(type)}
            `}>
                <FaFolder className="w-3 h-3 mr-1" />
                {type}
            </span>
        </div>
    )
}

// File metadata (last modified date and description)
function FileMetadata({ lastModified, description }: { lastModified: string; description?: string }) {
    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                    <FaClock className="w-3 h-3" />
                    <span>{formatDate(lastModified)}</span>
                </div>
            </div>
            {description && (
                <div className="text-sm text-gray-600 italic">
                    {description}
                </div>
            )}
        </div>
    )
}

// Right section with file size and action buttons
function FileActionsSection({ 
    resource, 
    onDownload,
    onDelete,
    onEditDescription
}: { 
    resource: ResourceFile; 
    onDownload: (e: React.MouseEvent) => void;
    onDelete: (e: React.MouseEvent) => void;
    onEditDescription: (e: React.MouseEvent) => void;
}) {
    return (
        <div className="flex flex-col items-end justify-center h-full min-w-0 flex-shrink-0">
            <FileSize size={resource.size} />
            <ActionButtons 
                resourceType={resource.type}
                onDownload={onDownload}
                onDelete={onDelete}
                onEditDescription={onEditDescription}
            />
        </div>
    )
}

// File size display with proper formatting
function FileSize({ size }: { size: number }) {
    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    return (
        <div className="text-lg font-semibold text-gray-700 mb-2">
            {formatFileSize(size)}
        </div>
    )
}

// Action buttons and indicators
function ActionButtons({ 
    resourceType, 
    onDownload,
    onDelete,
    onEditDescription
}: { 
    resourceType: string; 
    onDownload: (e: React.MouseEvent) => void;
    onDelete: (e: React.MouseEvent) => void;
    onEditDescription: (e: React.MouseEvent) => void;
}) {
    return (
        <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
                <EditDescriptionButton onEditDescription={onEditDescription} />
                <DownloadButton onDownload={onDownload} />
                <DeleteButton onDelete={onDelete} />
            </div>
            <ActionIndicator resourceType={resourceType} />
        </div>
    )
}

// Edit description button with hover effects
function EditDescriptionButton({ onEditDescription }: { onEditDescription: (e: React.MouseEvent) => void }) {
    return (
        <button
            onClick={onEditDescription}
            className="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-orange-100 rounded-lg transition-colors duration-200 group"
            title="Edit description"
        >
            <FaEdit className="w-3 h-3 text-gray-600 group-hover:text-orange-600" />
        </button>
    )
}

// Download button with hover effects
function DownloadButton({ onDownload }: { onDownload: (e: React.MouseEvent) => void }) {
    return (
        <button
            onClick={onDownload}
            className="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-orange-100 rounded-lg transition-colors duration-200 group"
            title="Download file"
        >
            <FaDownload className="w-3 h-3 text-gray-600 group-hover:text-orange-600" />
        </button>
    )
}

// Delete button with hover effects
function DeleteButton({ onDelete }: { onDelete: (e: React.MouseEvent) => void }) {
    return (
        <button
            onClick={onDelete}
            className="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-red-100 rounded-lg transition-colors duration-200 group"
            title="Delete file"
        >
            <FaTrash className="w-3 h-3 text-gray-600 group-hover:text-red-600" />
        </button>
    )
}

// Action indicator text (click to play/open)
function ActionIndicator({ resourceType }: { resourceType: string }) {
    return (
        <div className="flex items-center text-xs text-gray-400 group-hover:text-orange-500 transition-colors duration-200">
            {resourceType === 'mp4' ? 'Click to download' : 'Click to open'}
        </div>
    )
}

// Delete confirmation dialog component
function DeleteConfirmationDialog({ 
    isOpen, 
    fileName, 
    onConfirm, 
    onCancel 
}: { 
    isOpen: boolean; 
    fileName: string; 
    onConfirm: () => void; 
    onCancel: () => void; 
}) {
    return (
        <div onClick={(e) => e.stopPropagation()}>
            {/* Stops the user from triggering other actions when the dialog is open */}
            <Dialog open={isOpen} onOpenChange={onCancel}>
                <DialogContent 
                    className="max-w-md"
                    onInteractOutside={(e) => {
                        e.stopPropagation()
                        onCancel()
                    }}
                    onEscapeKeyDown={onCancel}
                    onClick={(e) => e.stopPropagation()}
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FaTrash className="w-5 h-5 text-red-500" />
                            Delete Resource
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "{fileName}"? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button 
                            variant="outline" 
                            onClick={onCancel}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={onConfirm}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        >
                            <FaTrash className="w-4 h-4 mr-2" />
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
